import { getMeltingBars, setMeltingBar } from './desertState.js';
import { getSandwormEffect } from './sandworm.js';
import { getResponsiveMultiplier } from '../visualizerUtils.js';

export function drawAudioBars(ctx, dataArray, width, height, time, deltaTime) {
  const barWidth = width / dataArray.length * getResponsiveMultiplier();
  let x = 0;
  
  for (let i = 0; i < dataArray.length; i++) {
    const intensity = dataArray[i] / 255;
    const barState = getMeltingBars().get(i);
    
    // Update melting state
    updateBarMeltingState(i, intensity, deltaTime, barState);
    
    // Calculate bar height with desert wind effect
    const windEffect = Math.sin(time * 1.2 + i * 0.12) * 12;
    let baseBarHeight = intensity * height * 0.6 + windEffect;
    
    // Apply melting effect
    if (barState.melting) {
      baseBarHeight *= (1 - barState.meltAmount * 0.7);
    }
    
    // Check if sandworm is affecting this bar
    const sandwormEffect = getSandwormEffect(x, barWidth, baseBarHeight);
    baseBarHeight *= sandwormEffect;
    
    const barHeight = Math.floor(Math.max(baseBarHeight, 5));
    const barY = height - barHeight;
    
    // Sandy desert colors
    const baseHue = 32 + (i / dataArray.length) * 28;
    const saturation = barState.melting ? 85 : 65;
    
    const gradient = ctx.createLinearGradient(0, barY, 0, height);
    gradient.addColorStop(0, `hsl(${baseHue}, ${saturation}%, 72%)`);
    gradient.addColorStop(0.5, `hsl(${baseHue + 8}, ${saturation - 8}%, 58%)`);
    gradient.addColorStop(1, `hsl(${baseHue + 16}, ${saturation - 16}%, 42%)`);
    
    ctx.fillStyle = gradient;
    ctx.fillRect(x, barY, barWidth, barHeight);
    
    // Draw melting effect
    if (barState.melting) {
      drawMeltingEffect(ctx, x, barY, barWidth, barHeight, barState.meltAmount, time, i);
    }
    
    // Sand particles on top of bars
    if (intensity > 0.4) {
      drawSandParticles(ctx, x + barWidth / 2, barY, intensity, baseHue, time);
    }
    
    x += barWidth;
  }
}

function updateBarMeltingState(barIndex, intensity, deltaTime, barState) {
  // Random chance for bars to start melting (desert heat effect)
  if (!barState.melting && intensity > 0.5) {
    const meltChance = 0.0006;
    if (Math.random() < meltChance) {
      barState.melting = true;
      barState.meltTimer = 0;
      barState.meltAmount = 0;
    }
  }
  
  // Update melting bars
  if (barState.melting) {
    barState.meltTimer += deltaTime;
    barState.meltAmount = Math.min(barState.meltTimer / 3.5, 1);
    
    if (barState.meltAmount >= 1) {
      barState.melting = false;
      barState.meltAmount = 0;
      barState.meltTimer = 0;
    }
  }
  
  setMeltingBar(barIndex, barState);
}

function drawMeltingEffect(ctx, x, barY, barWidth, barHeight, meltAmount, time, barIndex) {
  // Enhanced dripping sand effect
  const dripCount = 4;
  for (let i = 0; i < dripCount; i++) {
    const dripX = x + (barWidth / dripCount) * i + (barWidth / dripCount / 2);
    const dripLength = meltAmount * 25 + Math.sin(time * 3.5 + barIndex + i) * 6;
    const dripY = barY + barHeight;
    
    ctx.fillStyle = `hsla(42, 85%, 48%, ${0.9 * meltAmount})`;
    ctx.fillRect(dripX - 2.5, dripY, 5, dripLength);
    
    // Drip tip
    ctx.beginPath();
    ctx.arc(dripX, dripY + dripLength, 2, 0, Math.PI * 2);
    ctx.fill();
  }
  
  // Heat distortion effect
  if (meltAmount > 0.4) {
    ctx.save();
    ctx.globalAlpha = meltAmount * 0.7;
    ctx.fillStyle = `hsla(12, 100%, 55%, 0.35)`;
    ctx.fillRect(x, barY - 12, barWidth, barHeight + 24);
    ctx.restore();
  }
}

function drawSandParticles(ctx, x, y, intensity, hue, time) {
  ctx.save();
  
  for (let i = 0; i < Math.floor(intensity * 10); i++) {
    const particleX = x + (Math.random() - 0.5) * 25;
    const particleY = y + (Math.random() - 0.5) * 25 - Math.sin(time * 2.8 + i) * 12;
    const size = 1.2 + Math.random() * 2.5;
    
    ctx.fillStyle = `hsla(${hue + Math.random() * 18}, 75%, 62%, ${intensity * 0.8})`;
    ctx.beginPath();
    ctx.arc(particleX, particleY, size, 0, Math.PI * 2);
    ctx.fill();
  }
  
  ctx.restore();
}